import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getFileIcon } from "../../utils/fileIconUtils";
import { getLinkIcon } from "../../utils/businessCardUtils";
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
} from "react-icons/hi";
import VCardGenerator from "./VCardGenerator";
import { LinkItem } from "./LinkManager";
import BusinessCardFooter from "./BusinessCardFooter";
import BusinessCardShareButton from "./BusinessCardShareButton";
import UserRegistrationSection from "./UserRegistrationSection";
import BookingLinkButton from "./BookingLinkButton";
import { BusinessCardBooking } from "../../types/BusinessCardTypes";
import BusinessCardContainer from "./BusinessCardContainer";
import BusinessCardEmailLogin from "../pages/businessCardView/BusinessCardEmailLogin";
import ExpandableDescription from "./ExpandableDescription";
import LanguageSelector from "./LanguageSelector";
import SmartLink from "./SmartLink";
import PreviewSafeAnchor from "./PreviewSafeAnchor";
import PreviewSafeButton from "./PreviewSafeButton";

interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  enableUserRegistration?: boolean;
  showFooter?: boolean;
  booking?: BusinessCardBooking;
}

interface BusinessCardTechDisplayProps {
  userData: {
    user_identification: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };
  businessCardData: BusinessCardData;
  companyLogo?: string;
  companyName?: string;
  companyDescription?: string;
  className?: string;
  isPreview?: boolean;
  processId?: string;
  enableUserRegistration?: boolean;
  entityIdentification: string;
  ownerIdentification: string;
  onEmailClick?: () => void;
}

const BusinessCardTechDisplay: React.FC<BusinessCardTechDisplayProps> = (
  props
) => {
  const {
    userData,
    businessCardData,
    companyLogo,
    companyName,
    companyDescription,
    className = "",
    isPreview = false,
    processId,
    enableUserRegistration = false,
    entityIdentification,
    ownerIdentification,
    onEmailClick,
  } = props;

  const { t } = useTranslation("clientView");
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);

  const handleFlipToEmail = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setShowEmailLogin(true);
      onEmailClick?.();
    }, 300);
    setTimeout(() => setIsFlipping(false), 600);
  };

  const handleFileDownload = (cdnPath: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = cdnPath;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const primaryLinks = businessCardData.links?.slice(0, 3) || [];
  const additionalLinks = businessCardData.links?.slice(3) || [];
  const hasFiles =
    businessCardData.uploadedFiles && businessCardData.uploadedFiles.length > 0;

  const headerContent = (companyName || companyLogo || companyDescription) && (
    <div className="border-b border-cyan-400">
      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-green-400 to-blue-500 relative">
        <div className="absolute top-2 end-2 z-[9999]">
          <LanguageSelector variant="compact" />
        </div>
        <div className="flex items-center justify-center gap-3">
          {companyLogo && (
            <div className="w-10 h-10 flex-shrink-0">
              <img
                src={companyLogo}
                alt="Company Logo"
                className="w-full h-full object-contain rounded-lg bg-white shadow-sm"
              />
            </div>
          )}
          <div className="text-center min-w-0">
            {companyName && (
              <p
                className={`${
                  companyName.length > 20 ? "text-xs" : "text-base"
                } font-mono font-bold text-black uppercase tracking-wider text-start`}
              >
                <bdi>{companyName}</bdi>
              </p>
            )}
            {companyDescription && (
              <p className="text-xs text-black font-mono italic mt-1 max-w-72 mx-auto text-start">
                <bdi>"{companyDescription}"</bdi>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const mainContent = showEmailLogin ? (
    <div className="flex flex-col gap-3 sm:gap-4">
      <BusinessCardEmailLogin
        entityIdentification={entityIdentification}
        ownerIdentification={ownerIdentification}
        onBackToCard={() => {
          if (isFlipping) return;
          setIsFlipping(true);
          setTimeout(() => setShowEmailLogin(false), 300);
          setTimeout(() => setIsFlipping(false), 600);
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="pb-3 border-b border-cyan-400 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl px-1 py-4 border ">
        <div className="flex items-center gap-4">
          {userData.avatar ? (
            <img
              src={userData.avatar}
              alt={`${userData.first_name} ${userData.last_name}`}
              className="w-16 h-16 rounded-full border-2 border-cyan-400 object-cover shadow-lg shadow-cyan-400/50"
            />
          ) : (
            <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center border-2 border-cyan-400 shadow-lg shadow-cyan-400/50">
              {userData.first_name?.[0] || userData.last_name?.[0] ? (
                <span className="text-xl font-mono font-bold text-cyan-300 uppercase">
                  {userData.first_name?.[0]}
                  {userData.last_name?.[0]}
                </span>
              ) : (
                <HiOutlineUser className="w-8 h-8 text-cyan-300" />
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-lg font-mono font-bold text-green-400 break-words">
              <bdi>
                {userData.first_name} {userData.last_name}
              </bdi>
            </h1>
            <p className="text-sm text-cyan-400 break-words font-mono text-start">
              <bdi>{businessCardData.jobTitle}</bdi>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <PreviewSafeAnchor
          href={`mailto:${userData.email}`}
          className="grid grid-cols-[auto_1fr] gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-500 hover:to-cyan-500 transition-all duration-200 border border-cyan-400 items-center"
        >
          <HiOutlineMail className="w-5 h-5" />
          <div className="min-w-0">
            <p
              dir="auto"
              className="text-xs text-cyan-100 font-medium text-start"
            >
              {t("key98")}
            </p>
            <p
              className="font-mono text-xs sm:text-sm truncate"
              title={userData.email}
            >
              <bdi>{userData.email}</bdi>
            </p>
          </div>
        </PreviewSafeAnchor>

        {userData.phone && (
          <PreviewSafeAnchor
            href={`tel:${userData.phone}`}
            className="flex items-center gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-green-600 to-teal-600 text-white rounded-lg hover:from-green-500 hover:to-teal-500 transition-all duration-200 border border-cyan-400"
          >
            <HiOutlinePhone className="w-5 h-5" />
            <p className="font-mono text-xs sm:text-sm">
              <bdi>{userData.phone}</bdi>
            </p>
          </PreviewSafeAnchor>
        )}
      </div>

      {businessCardData.description && (
        <div className="border-t border-cyan-400 p-1 bg-gray-800 rounded-xl border w-full min-w-0 overflow-hidden">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <p className="text-sm font-mono font-bold text-cyan-400 uppercase tracking-widest text-start">
              {t("businessCard.sections.systemInfo").toUpperCase()}
            </p>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
          </div>

          <ExpandableDescription
            className="w-full"
            content={businessCardData.description}
            maxLength={150}
            maxLines={5}
            textClassName="text-sm text-green-400 leading-relaxed font-mono"
            buttonClassName="mt-2 inline-flex items-center text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors duration-200 font-mono"
            expandText={t("key97") as string}
            collapseText={t("key107") as string}
          />
        </div>
      )}

      {primaryLinks.length > 0 && (
        <div className="space-y-2">
          <div className="text-center">
            <p
              dir="auto"
              className="text-sm font-mono font-bold text-cyan-400 uppercase tracking-widest bg-gray-800 inline-block px-4 py-1 rounded-full border border-cyan-400 text-start"
            >
              &gt; {t("businessCard.links").toUpperCase()}_
            </p>
          </div>
          {primaryLinks.map((link, index) => {
            const techGradients = [
              "from-blue-600 to-cyan-600",
              "from-green-600 to-teal-600",
              "from-purple-600 to-pink-600",
            ];
            const gradient = techGradients[index % techGradients.length];

            return (
              <SmartLink
                key={index}
                href={link.link}
                fallbackHref={link.fallbackLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 sm:gap-3 p-2 bg-gradient-to-r ${gradient} hover:scale-105 text-white rounded-lg transition-all duration-200 group border border-cyan-400`}
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                  {getLinkIcon(link, "text-blue-400")}
                </div>
                <p className="flex-1 text-sm font-mono font-bold text-white group-hover:text-cyan-200 truncate text-start">
                  {link.label}
                </p>
                <div className="text-cyan-400 font-mono font-bold">→</div>
              </SmartLink>
            );
          })}
        </div>
      )}

      {additionalLinks.length > 0 && (
        <div>
          {showAllLinks && (
            <div className="space-y-2 mb-2">
              {additionalLinks.map((link, index) => {
                const techGradients = [
                  "from-indigo-600 to-blue-600",
                  "from-pink-600 to-red-600",
                  "from-yellow-600 to-orange-600",
                ];
                const gradient = techGradients[index % techGradients.length];

                return (
                  <SmartLink
                    key={index + 3}
                    href={link.link}
                    fallbackHref={link.fallbackLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 sm:gap-3 p-2 bg-gradient-to-r ${gradient} hover:scale-105 text-white rounded-lg transition-all duration-200 group border border-cyan-400`}
                  >
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow-sm">
                      {getLinkIcon(link, "text-blue-400")}
                    </div>
                    <p className="flex-1 text-sm font-mono font-bold text-white truncate group-hover:text-cyan-200 text-start">
                      {link.label}
                    </p>
                    <div className="text-cyan-400 font-mono font-bold">→</div>
                  </SmartLink>
                );
              })}
            </div>
          )}
          <PreviewSafeButton
            onClick={() => setShowAllLinks(!showAllLinks)}
            className="flex items-center justify-center w-full p-2 text-sm text-cyan-400 font-mono font-bold bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-200 border border-cyan-400"
            dir="auto"
          >
            <span className="me-1 text-start">
              {showAllLinks
                ? t("businessCard.showLess")
                : t("businessCard.showMoreLinks", {
                    count: additionalLinks.length,
                  })}
            </span>
            {showAllLinks ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>
        </div>
      )}

      {hasFiles && (
        <div>
          <PreviewSafeButton
            onClick={() => setShowFiles(!showFiles)}
            className="flex items-center justify-between w-full p-2 text-sm text-green-400 font-mono font-bold bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-200 border border-cyan-400"
            dir="auto"
          >
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-4 h-4" />
              <span className="text-start">
                {t("businessCard.files", {
                  count: businessCardData.uploadedFiles?.length || 0,
                })}
              </span>
            </div>
            {showFiles ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>

          {showFiles && businessCardData.uploadedFiles && (
            <div className="mt-2 space-y-1">
              {businessCardData.uploadedFiles.map((file, index) => (
                <PreviewSafeButton
                  key={index}
                  onClick={() =>
                    handleFileDownload(file.cdnPath, file.fileName)
                  }
                  className="w-full flex items-center gap-2 p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-200 text-left group border border-cyan-400"
                  dir="auto"
                >
                  <span className="text-sm">{getFileIcon(file.fileName)}</span>
                  <p className="flex-1 text-sm font-mono font-bold text-cyan-400 truncate group-hover:text-green-400 text-start">
                    {file.displayName}
                  </p>
                  <HiOutlineDownload className="w-4 h-4 text-cyan-400 group-hover:text-green-400" />
                </PreviewSafeButton>
              ))}
            </div>
          )}
        </div>
      )}

      {(() => {
        const showSaveContact = businessCardData.allowVCardDownload === true;
        const showShare = !!processId;
        if (!showSaveContact && !showShare) return null;

        return (
          <div
            className={`grid gap-2 ${
              showSaveContact && showShare ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {showSaveContact && (
              <VCardGenerator
                data={{
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  jobTitle: businessCardData.jobTitle,
                  email: userData.email,
                  phone: userData.phone,
                  description: businessCardData.description,
                  links: businessCardData.links,
                  avatar: userData.avatar,
                }}
                buttonColor="cyan"
              />
            )}
            {showShare && (
              <BusinessCardShareButton
                processId={processId}
                size="md"
                variant="secondary"
                logoUrl={companyLogo}
              />
            )}
          </div>
        );
      })()}

      <BookingLinkButton
        booking={businessCardData.booking}
        entityIdentification={entityIdentification}
        memberId={userData.user_identification}
      />

      {enableUserRegistration && processId && entityIdentification && (
        <UserRegistrationSection
          processId={processId}
          entityData={{
            entity_identification: entityIdentification,
            entity_name: companyName,
            entity_logo: companyLogo,
          }}
          theme="minimal"
          size="sm"
          onEmailLogin={handleFlipToEmail}
        />
      )}
    </div>
  );

  const frontContent = (
    <div className="w-full">
      <div
        className="
       w-full h-full relative rounded-xl overflow-hidden bg-gradient-to-br from-gray-900 to-black border border-cyan-400 shadow-lg shadow-cyan-400/25
      "
      >
        {headerContent && (
          <div className="row-start-1 row-end-2">{headerContent}</div>
        )}

        <div className="row-start-2 row-end-3 min-h-0">
          <div
            className="h-full overflow-auto touch-pan-y"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            <div className="px-3 sm:px-4 py-3 sm:py-4">{mainContent}</div>
          </div>
        </div>

        <div className="row-start-3 row-end-4">
          <BusinessCardFooter
            showFooter={businessCardData.showFooter as boolean}
            theme="tech"
          />
        </div>
      </div>
    </div>
  );

  return (
    <BusinessCardContainer
      isPreview={isPreview}
      className={`w-full h-auto min-h-0 ${className}`}
      frontContent={frontContent}
      isFlipping={isFlipping}
      showEmailLogin={showEmailLogin}
    />
  );
};

export default BusinessCardTechDisplay;
